import React from "react"
import { UTCDate } from "@date-fns/utc";
import * as Yup from 'yup'
import Ajax from "../../../../Ajax"
import { format, isValid } from "date-fns"
import { Alert, DisplayDictionary, DisplayValue } from "../../../.."
import { EnquiryInitiateDto } from "../../../../models/Enquiry"
import { Button, CircularProgress } from "@mui/material"
import Field from "../../fields/Field"
import { useFormik } from "formik"
import EnquirySettingsForm from "./EnquirySettingsFormSection"
import { store } from "../../../../stores/Store"
import EnquiryStatusesFormSection from "./EnquiryStatusesFormSection";


export default function EnquiryAssessmentFormSection ({
    hidden,
    caseUid,
    onCancel = () => {},
    onSubmit = (enquiry) => {}
}) {

    const [loading, setLoading] = React.useState(true)
    const [enquiry, setEnquiry] = React.useState<EnquiryInitiateDto>(null)
    const [showSettings, setShowSettings] = React.useState(false)
    const [defaultSettings, setDefaultSettings] = React.useState(null)

    const interestDateMin = new Date("1950-01-01")
    const interestDateMax = new UTCDate(store.AppStore.sessionInfo.businessDate)
    const initialInterestDate = new UTCDate(store.AppStore.sessionInfo.businessDate)

    const getEnquiryOptions = async (interestDate, ruleSystem) => {
        setLoading(true)

        if (interestDate && isValid(interestDate) && interestDate > interestDateMin) {
            await Ajax.Enquiry.GetOptions(caseUid, ruleSystem, format(interestDate, "yyyy-MM-dd")).then((response) => {

                var defaultSettings = {}
                response.data.list2.forEach((setting) => {
                    defaultSettings[setting.uid] = setting.value
                })
                setDefaultSettings(defaultSettings)

                setEnquiry({
                    interestDate: interestDate,
                    ruleSystem: ruleSystem,
                    statuses: response.data.list1,
                    settings: response.data.list2
                })

                setLoading(false)
                formik.setFieldValue("status", null)
    
            }).catch((response) => {
                Alert({message: response.data?.detail ? response.data.detail : "Error getting enquiry options"})
            })
        }
    }

    const formik = useFormik({

        validateOnMount: true,
        initialValues: {
            ruleSystem: "UK",
            interestDate: initialInterestDate,
            statuses: []
        },

        validationSchema: Yup.object({
            ruleSystem: Yup.string().required("Please choose a legal system"),
            interestDate: Yup.date()
                .required("This field is required")
                .min(interestDateMin, `Must be after ${format(interestDateMin, "yyyy-MM-dd")}`)
                .max(interestDateMax, `Cannot be after ${format(interestDateMax, "yyyy-MM-dd")}`)
                .typeError("Please choose an interest date"),
            statuses: Yup.array().of(Yup.string()).min(1, "Please select the legal status to be evaluated")
        }),

        onSubmit: (values, actions) => {
            
            var newStatusNodes = [...enquiry.statuses].map(status => {
                return { ...status, selected: values.statuses.includes(status.code) }
            })

            var selectedStatusNode = newStatusNodes.find(sn => sn.selected === true)

            if (!selectedStatusNode) {
                formik.setFieldError("status", "You must select a status")
                return
            }
            
            onSubmit({...enquiry, statuses: newStatusNodes})

            store.QuestionnaireStore.setSearchTemplateKeysByStatusId(selectedStatusNode.code)
        }
    })

    React.useEffect(() => {
        getEnquiryOptions(formik.values.interestDate, formik.values.ruleSystem)
    }, [])

    if (hidden) return <></>

    // var investigationExtentSetting = enquiry?.settings?.find(setting => setting.code === "investigation_extent")

    if (!enquiry) return <CircularProgress style={{width: "20px", height: "20px", margin: "50px auto", display: "flex"}} />

    return (
        <form onSubmit={formik.handleSubmit} className="form">
            <h2>Create Enquiry<span style={{color: "grey", fontWeight: 400}}> - Assessment</span></h2>

            <div className={loading ? "disabled" : ""} style={{display: !showSettings ? "block" : "none"}}>
                <EnquiryStatusesFormSection
                    enquiry={enquiry} 
                    setEnquiry={setEnquiry} 
                    formik={formik} 
                    defaultSettings={defaultSettings} />
            </div>

            {showSettings ? (
                <div>
                    <div className="form-fields">
                        <h4>Legal context</h4>

                        <div style={{display: "grid", gridTemplateColumns: "250px 250px", gap: "10px"}}>
                            <Field.Date
                                formik
                                name="interestDate"
                                label="Interest date"
                                value={formik.values.interestDate}
                                warning={formik.errors.interestDate ? formik.errors.interestDate : false}
                                onChange={(e) => {
                                    // formik.handleChange(e)
                                    console.log(e.target.date)
                                    formik.setFieldValue("interestDate", new UTCDate(e.target.value))
                                    getEnquiryOptions(new UTCDate(e.target.value), formik.values.ruleSystem)
                                }} />

                            <Field.Select
                                formik
                                name="ruleSystem"
                                label="Rule System"
                                value={formik.values.ruleSystem}
                                warning={formik.errors.ruleSystem ? formik.errors.ruleSystem : false}
                                options={DisplayDictionary("legal_system", true)} 
                                onChange={(e) => {
                                    formik.handleChange(e)
                                    getEnquiryOptions(formik.values.interestDate, e.target.value)
                                }} />
                        </div>
                    </div>

                    <div className={loading ? "disabled" : ""}>
                        <EnquirySettingsForm 
                            settingNodes={enquiry.settings}
                            onSubmit={(settingNodes) => {
                                setEnquiry({...enquiry, settings: settingNodes})
                                setShowSettings(false)
                            }} /> 
                    </div>
                </div>     
            ) : null}

            {!showSettings ? (
                <div className="form-buttons">
                    <div className="form-buttons">
                        <Button 
                            className="btn btn-sm" 
                            onClick={() => onCancel()}>
                            Cancel
                        </Button>
                        
                        <Button 
                            className="btn btn-sm" 
                            disabled={!enquiry}
                            onClick={() => setShowSettings(true)}>
                            Advanced settings
                        </Button>
                    </div>
                    
                    <Button 
                        className="btn btn-sm" 
                        variant="contained" 
                        color="success"
                        disabled={!enquiry}
                        type="submit">
                        Next
                    </Button>
                </div>           
            ) : null}
        </form>
    )
}